import { redirect, useLoaderData } from "react-router-dom";

import {
  getAdonisTopRecommendationsId,
  getPlatform,
  getShopCategories,
  getShopCustomCategoriesListings,
  getShopDiscounts,
  getUser,
} from "api/methods";
import { useTranslation } from "@/core/i18n";
import { RepeatMDFooter } from "atoms/repeatmd-footer/RepeatMDFooter";
import { ShopMembershipListItem } from "molecules/containers/shop-membership-list-item/ShopMembershipListItem";
import { queryClient } from "pages/Root";
import { useFeatureFlag } from "services/Flags";
import { getMembershipsWithDiscountInformation } from "services/ListingHelperService";
import { formatCurrency } from "toolbox/Money";
import { waitObj } from "toolbox/Promise";
import { defineLoader } from "toolbox/Router";
import { parseBillingPeriodName } from "utils/billingPeriod.helpers";

import { ShopListHeader } from "./ShopListHeader/ShopListHeader";
import { membershipsWithUnlockedQuery } from "modules/shopVisibility";

type LoaderData = Awaited<ReturnType<typeof load>>;

const load = () => {
  // Same merge as the shop home: without it, a membership the patient unlocked by link is on the
  // shop landing page but missing from the memberships list they navigate to from it.
  const memberships = queryClient.fetchQuery(membershipsWithUnlockedQuery());
  const categories = queryClient.fetchQuery({ ...getShopCategories.keyed() });
  const shopDiscounts = queryClient.fetchQuery({
    ...getShopDiscounts.keyed(),
    staleTime: 1000 * 10,
  });
  const customShopCategories = queryClient.fetchQuery({
    ...getShopCustomCategoriesListings.keyed(),
    staleTime: 1000 * 10,
  });
  const user = queryClient.fetchQuery({
    ...getUser.keyed(),
    staleTime: 1000 * 60 * 2,
  });
  const recommendationIds = queryClient.fetchQuery({
    ...getAdonisTopRecommendationsId.keyed({ limit: 10 }),
    staleTime: 1000 * 30,
  });

  return waitObj({
    memberships,
    categories,
    shopDiscounts,
    customShopCategories,
    user,
    recommendationIds,
  });
};

export const shopMembershipsLoader = defineLoader(async () => {
  const platform = await queryClient.fetchQuery({ ...getPlatform.keyed() });

  // Redirect to home if memberships feature is disabled.
  // Users with existing memberships can still view them in wallet.
  if (platform.platformSettings?.membershipsEnabled === false) {
    return redirect("/home");
  }

  return load();
});

const ShopMembershipsPage = () => {
  const { t } = useTranslation();
  const { memberships, categories, shopDiscounts, customShopCategories, user, recommendationIds } =
    useLoaderData() as LoaderData;

  const isFlexibleMembershipsEnabled = useFeatureFlag("flexible_membership_terms");

  const membershipsCategory = categories.find((c) => c.defaultCategoryId === "memberships");

  const membershipsWithDiscountInformation = getMembershipsWithDiscountInformation(
    memberships,
    shopDiscounts,
    customShopCategories,
  );

  const recommendedIds = new Set(recommendationIds);

  return (
    <div className="flex flex-col bg-two">
      {membershipsCategory && (
        <ShopListHeader
          title={membershipsCategory.title}
          subtitle={membershipsCategory.description}
          bgGradient
          // TODO(CAA-158) implement a backgroundImageUrl
          backgroundColor="rgb(var(--repeatmd-brand-color))"
          backgroundPattern
        />
      )}
      <div className="mx-auto flex w-full max-w-6xl flex-col px-4 py-5 desk:px-6 desklg:px-8 desklg:py-10">
        <div className="grid grid-cols-1 items-stretch gap-5 desk:grid-cols-2 desklg:grid-cols-3 deskxl:grid-cols-4">
          {membershipsWithDiscountInformation.map((membership) => {
            const includedList = membership.membershipBenefits.map(
              (benefit) => benefit.description,
            );
            const beautyBankCredit = membership.membershipRewardGroups?.find(
              (item) =>
                item.beautyBank?.creditAmount !== null && item.membershipRewards.length === 0,
            )?.beautyBank?.creditAmount;

            if (beautyBankCredit !== null && beautyBankCredit !== undefined) {
              includedList.unshift(
                t("shopMembershipsPage.beautyBankCredit", {
                  amount: formatCurrency(beautyBankCredit, 0),
                }),
              );
            }
            return (
              <ShopMembershipListItem
                key={membership.id}
                membershipId={membership.id}
                title={membership.name}
                includedList={includedList}
                imageUrl={membership.imageUrl}
                price={
                  isFlexibleMembershipsEnabled
                    ? membership.billingPeriodCost
                    : membership.monthlyCost
                }
                setupFeeInCents={membership.setupFeeInCents}
                billingPeriodInterval={parseBillingPeriodName(membership.billingPeriodInterval)}
                billingPeriodIntervalCount={membership.billingPeriodIntervalCount}
                to={`/pdp/membership/${membership.id}`}
                discount={membership.discountText}
                customColor={membership.customColor}
                guestMode={user.isGuest}
                isRecommended={recommendedIds.has(membership.id)}
              />
            );
          })}
        </div>
      </div>
      <RepeatMDFooter />
    </div>
  );
};

export default ShopMembershipsPage;
